// SEO Helpers for dynamic meta tag management
export const updatePageSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  noindex = false
}) => {
  // Update document title
  if (title) {
    document.title = title.includes('Honeybear.ai') ? title : `${title} | Honeybear.ai`;
  }

  // Update meta description
  if (description) {
    updateMetaTag('name', 'description', description);
  }

  // Update meta keywords
  if (keywords) {
    updateMetaTag('name', 'keywords', keywords);
  }

  // Update robots tag
  const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow';
  updateMetaTag('name', 'robots', robotsContent);

  // Update canonical URL
  if (canonical) {
    updateLinkTag('canonical', canonical);
  }

  // Update Open Graph tags
  updateMetaTag('property', 'og:title', title || document.title);
  updateMetaTag('property', 'og:description', description || '');
  updateMetaTag('property', 'og:type', ogType);
  updateMetaTag('property', 'og:url', canonical || window.location.href);
  
  if (ogImage) {
    updateMetaTag('property', 'og:image', ogImage);
  }

  // Update Twitter Card tags
  updateMetaTag('name', 'twitter:card', twitterCard);
  updateMetaTag('name', 'twitter:title', title || document.title);
  updateMetaTag('name', 'twitter:description', description || '');
  
  if (ogImage) {
    updateMetaTag('name', 'twitter:image', ogImage);
  }

  // Add structured data
  if (structuredData) {
    addStructuredData(structuredData);
  }
};

const updateMetaTag = (attribute, value, content) => {
  if (!content) return;
  
  let tag = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const updateLinkTag = (rel, href) => {
  if (!href) return;
  
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const addStructuredData = (data) => {
  // Remove existing structured data for this page
  const existingScript = document.querySelector('script[type="application/ld+json"][data-dynamic="true"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-dynamic', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Predefined SEO configurations for different page types
export const seoConfigs = {
  home: {
    title: 'Honeybear.ai | AI Assistant For Your Documents',
    description: 'Advanced AI document assistant that helps you analyze, summarize, and chat with your PDFs, documents, and videos. Get instant answers with precise citations from your research materials.',
    keywords: 'AI document assistant, PDF analysis, document chat, AI research tool, PDF summarizer, document analysis, AI study assistant, research assistant',
    canonical: 'https://www.honeybear.ai/',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Honeybear.ai",
      "url": "https://www.honeybear.ai",
      "description": "Advanced AI document assistant that helps you analyze, summarize, and chat with your PDFs, documents, and videos.",
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web",
      "featureList": [
        "PDF Analysis",
        "Document Chat", 
        "Video Summarization",
        "Research Assistant",
        "Citation Generation"
      ]
    }
  },
  
  aiSummarizer: {
    title: 'AI Document Summarizer | Honeybear.ai',
    description: 'Instantly summarize PDFs, documents, and research papers with our advanced AI document summarizer. Get key insights and main points in seconds.',
    keywords: 'AI document summarizer, PDF summarizer, document analysis, research paper summary, AI text summarization',
    canonical: 'https://www.honeybear.ai/ai-summarizer',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg'
  },

  aiVideoSummarizer: {
    title: 'AI Video Summarizer | Honeybear.ai', 
    description: 'Summarize YouTube videos, lectures, and presentations with AI. Get transcripts, key points, and insights from any video content.',
    keywords: 'AI video summarizer, YouTube summarizer, video transcript, lecture summary, video analysis',
    canonical: 'https://www.honeybear.ai/ai-video-summarizer',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg'
  },

  researchers: {
    title: 'AI Research Assistant | Honeybear.ai',
    description: 'Advanced AI research assistant for academics and professionals. Analyze multiple documents, generate citations, and accelerate your research workflow.',
    keywords: 'AI research assistant, academic research, document analysis, research tool, citation generator',
    canonical: 'https://www.honeybear.ai/researchers',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg'
  },

  aiLegalResearch: {
    title: 'AI Legal Research Assistant | Honeybear.ai',
    description: 'Streamline legal research with AI. Analyze legal documents, case law, and contracts. Get precise citations and legal insights.',
    keywords: 'AI legal research, legal document analysis, case law research, contract analysis, legal AI assistant',
    canonical: 'https://www.honeybear.ai/ai-legal-research',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg'
  },

  pricing: {
    title: 'Pricing Plans | Honeybear.ai',
    description: 'Choose the perfect plan for your AI document analysis needs. Flexible pricing for students, professionals, and teams.',
    keywords: 'AI document assistant pricing, subscription plans, academic pricing, professional tools',
    canonical: 'https://www.honeybear.ai/pricing',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg'
  },

  blog: {
    title: 'AI & Research Blog | Honeybear.ai',
    description: 'Learn about AI tools, research techniques, and productivity tips. Expert insights on document analysis, study methods, and academic tools.',
    keywords: 'AI research blog, study tips, academic tools, research techniques, AI document analysis',
    canonical: 'https://www.honeybear.ai/blog',
    ogImage: 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Honeybear.ai Blog",
      "description": "Expert insights on AI tools, research techniques, and productivity.",
      "url": "https://www.honeybear.ai/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Honeybear.ai",
        "url": "https://www.honeybear.ai"
      }
    }
  }
};

// Generate blog post structured data
export const generateBlogPostStructuredData = (post) => {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription || post.subtitle,
    "image": post.featuredImage || 'https://www.honeybear.ai/Official_Logo_Honeybear.jpg',
    "author": {
      "@type": "Person",
      "name": post.author || 'Molly Jorgensen'
    },
    "publisher": {
      "@type": "Organization",
      "name": "Honeybear.ai",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.honeybear.ai/Official_Logo_Honeybear.jpg"
      }
    },
    "datePublished": post.publishDate || post.createdAt,
    "dateModified": post.updatedAt || post.createdAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.honeybear.ai/blog/${post.slug}`
    },
    "keywords": post.tags ? post.tags.join(', ') : '',
    "articleSection": post.category || 'General'
  };
};

// SEO Hook for React components (import React in your component to use this)
export const useSEO = (config) => {
  // This hook should be used in React components where React is already imported
  // Example: const { useEffect } = React; or import { useEffect } from 'react';
  
  if (typeof window !== 'undefined' && config) {
    // For non-hook usage, call updatePageSEO directly
    updatePageSEO(config);
  }
}; 